import { useEffect, useState, useCallback, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import VERTC, { MediaType } from '@volcengine/rtc';
import { Modal, Message } from '@arco-design/web-react';
import logger from '@/utils/logger';
import { sleep } from '@/utils/networkUtils';
import RtcClient from '@/lib/RtcClient';
import { overlayManager } from '@/lib/managers/OverlayManager';
import { ConnectionState } from '@/lib/core';
import {
  localJoinRoom,
  updateLocalUser,
  updateRTCConfig,
  RTCConfig,
  SceneConfig,
  updateSceneConfig,
  resetRoomState,
} from '@/store/slices/room';
import {
  updateMediaInputs,
  updateSelectedDevice,
  setDevicePermissions,
} from '@/store/slices/device';
import useRtcListeners from '@/lib/listenerHooks';
import store, { RootState } from '@/store';

export const ABORT_VISIBILITY_CHANGE = 'abortVisibilityChange';
export interface FormProps {
  username: string;
  roomId: string;
  publishAudio: boolean;
}

export const useScene = () => {
  const { scene, sceneConfigMap } = useSelector((state: RootState) => state.room);
  const sceneConfig = sceneConfigMap[scene] || {};

  // 本地设置完全独立，不依赖场景配置
  const getLocalInterruptMode = () => {
    const sessionStoragePref =
      typeof window !== 'undefined' ? sessionStorage.getItem('voiceInterruptAllowed') : null;
    if (sessionStoragePref !== null) {
      return sessionStoragePref === '1';
    }
    // 如果没有本地设置，返回默认值 false
    return false;
  };

  return {
    ...sceneConfig,
    isInterruptMode: getLocalInterruptMode(),
  };
};

export const useRTC = () => {
  const { scene, rtcConfigMap } = useSelector((state: RootState) => state.room);
  return rtcConfigMap[scene] || {};
};

export const useDeviceState = () => {
  const dispatch = useDispatch();
  const room = useSelector((state: RootState) => state.room);
  const localUser = room.localUser;
  const isAudioPublished = localUser.publishAudio;
  // 业务裁剪：视频/屏幕共享下线
  const queryDevices = async (type: MediaType) => {
    const mediaDevices = await RtcClient.getDevices({
      audio: type === MediaType.AUDIO,
      video: type === MediaType.VIDEO,
    });
    if (type === MediaType.AUDIO) {
      dispatch(
        updateMediaInputs({
          audioInputs: mediaDevices.audioInputs,
        })
      );
      dispatch(
        updateSelectedDevice({
          selectedMicrophone: mediaDevices.audioInputs[0]?.deviceId,
        })
      );
    } else {
      dispatch(
        updateMediaInputs({
          videoInputs: mediaDevices.videoInputs,
        })
      );
      dispatch(
        updateSelectedDevice({
          selectedCamera: mediaDevices.videoInputs[0]?.deviceId,
        })
      );
    }
    return mediaDevices;
  };

  const switchMic = async (controlPublish = true) => {
    if (controlPublish) {
      await (!isAudioPublished
        ? RtcClient.publishStream(MediaType.AUDIO)
        : RtcClient.unpublishStream(MediaType.AUDIO));
    }
    queryDevices(MediaType.AUDIO);
    await (!isAudioPublished ? RtcClient.startAudioCapture() : RtcClient.stopAudioCapture());
    dispatch(
      updateLocalUser({
        publishAudio: !isAudioPublished,
      })
    );
  };

  // 摄像头逻辑移除

  // 屏幕共享逻辑移除

  return {
    isAudioPublished,
    switchMic,
  };
};

export const useGetDevicePermission = () => {
  const [permission, setPermission] = useState<{
    audio: boolean;
  }>();

  const dispatch = useDispatch();

  useEffect(() => {
    (async () => {
      const permission = await RtcClient.checkPermission();
      dispatch(setDevicePermissions(permission));
      setPermission(permission);
    })();
  }, [dispatch]);
  return permission;
};

// 重连配置
const RECONNECT_CONFIG = {
  maxRetries: 3,
  baseDelay: 1000, // 1秒
  maxDelay: 10000, // 10秒
};

// 封装带重试的token获取逻辑（递归setTimeout实现，避免for+await linter警告）
// 使用指数退避策略提升重试效率
function getTokenWithRetry(
  getTokenFn: () => Promise<any>,
  maxRetries = 3,
  baseDelay = 1000 // 基础延迟改为 1 秒
): Promise<true | any> {
  let attempt = 0;
  const maxDelay = 10000; // 最大延迟 10 秒

  return new Promise((resolve) => {
    const tryGetToken = () => {
      getTokenFn()
        .then(() => resolve(true))
        .catch((e) => {
          attempt++;
          if (attempt < maxRetries) {
            // 指数退避：2^attempt * baseDelay + 随机抖动（避免雷鸣群效应）
            const exponentialDelay = Math.min(
              baseDelay * (2 ** (attempt - 1)),
              maxDelay
            );
            const jitter = Math.random() * 1000; // 0-1秒的随机抖动
            const delayMs = exponentialDelay + jitter;

            logger.info(
              `[Common] 网络异常，${Math.round(delayMs)}ms 后重试 (第${attempt}/${maxRetries}次)`
            );
            Message.info(`网络异常，正在重试... (${attempt}/${maxRetries})`);
            setTimeout(tryGetToken, delayMs);
          } else {
            logger.error(`[Common] 重试${maxRetries}次后仍然失败`, e);
            resolve(e);
          }
        });
    };
    tryGetToken();
  });
}

export const useJoin = (): [boolean, () => Promise<void | boolean>] => {
  const dispatch = useDispatch();
  useDeviceState();
  const [joining, setJoining] = useState(false);
  const [connectionState, setConnectionState] = useState<ConnectionState>(ConnectionState.IDLE);
  const [reconnectAttempts, setReconnectAttempts] = useState(0);
  const listeners = useRtcListeners();
  const reconnectTimerRef = useRef<number | null>(null);

  // 使用 useRef 来避免循环依赖
  const disPatchJoinRef = useRef<(() => Promise<boolean | undefined>) | null>(null);

  // 基础重连机制
  const handleReconnect = useCallback(async () => {
    if (reconnectAttempts >= RECONNECT_CONFIG.maxRetries) {
      logger.error('[Common] 重连次数已达上限');
      setConnectionState(ConnectionState.ERROR);
      return;
    }

    setConnectionState(ConnectionState.CONNECTING);
    setReconnectAttempts((prev) => prev + 1);

    const delay = Math.min(
      RECONNECT_CONFIG.baseDelay * 2 ** reconnectAttempts,
      RECONNECT_CONFIG.maxDelay
    );

    logger.info(`[Common] ${delay}ms 后尝试重连 (第${reconnectAttempts + 1}次)`);

    reconnectTimerRef.current = window.setTimeout(async () => {
      try {
        // 使用 ref 来避免依赖问题
        const result = await disPatchJoinRef.current?.();
        if (result !== false) {
          setConnectionState(ConnectionState.CONNECTED);
          setReconnectAttempts(0);
          logger.info('[Common] 重连成功');
        }
      } catch (error) {
        logger.warn('[Common] 重连失败:', error);
        handleReconnect(); // 递归重连
      }
    }, delay);
  }, [reconnectAttempts]);

  // 网络状态检测
  const handleOnlineStatus = useCallback(() => {
    if (navigator.onLine) {
      logger.info('[Common] 网络已恢复');
      if (connectionState === ConnectionState.ERROR) {
        // 网络恢复时尝试重连
        handleReconnect();
      }
    } else {
      logger.warn('[Common] 网络已断开');
      setConnectionState(ConnectionState.ERROR);
    }
  }, [connectionState, handleReconnect]);

  // 监听网络状态变化
  useEffect(() => {
    window.addEventListener('online', handleOnlineStatus);
    window.addEventListener('offline', handleOnlineStatus);

    return () => {
      window.removeEventListener('online', handleOnlineStatus);
      window.removeEventListener('offline', handleOnlineStatus);
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
    };
  }, [handleOnlineStatus]);

  const handleAIGCModeStart = async () => {
    const sceneFromStore = store.getState().room.scene;
    if (!sceneFromStore) {
      logger.warn('[Common] 未获取到场景，跳过启动 AIGC');
      return;
    }
    await RtcClient.startAgent(sceneFromStore);
  };

  // 添加超时控制的引擎创建
  const createEngineWithTimeout = async (timeout = 10000) => {
    const timeoutPromise = new Promise((_, reject) => {
      setTimeout(() => reject(new Error('创建引擎超时')), timeout);
    });

    return Promise.race([RtcClient.createEngine(), timeoutPromise]);
  };

  // 添加超时控制的房间加入
  const joinRoomWithTimeout = async (timeout = 15000) => {
    const timeoutPromise = new Promise((_, reject) => {
      setTimeout(() => reject(new Error('加入房间超时')), timeout);
    });

    return Promise.race([RtcClient.joinRoom(), timeoutPromise]);
  };

  // 安全的引擎创建和房间加入
  const safeEngineCreation = async () => {
    try {
      await createEngineWithTimeout();
      logger.info('[Common] 引擎创建成功');
    } catch (error) {
      logger.error('[Common] 引擎创建失败:', error);
      // 如果是PeerConnection相关错误，等待一段时间后重试
      if (error instanceof Error && error.message.includes('connectionState')) {
        logger.debug('[Common] 检测到PeerConnection错误，等待1秒后重试...');
        await sleep(1000);
        await createEngineWithTimeout();
      } else {
        throw error;
      }
    }
  };

  const safeRoomJoin = async () => {
    try {
      await joinRoomWithTimeout();
      logger.info('[Common] 房间加入成功');
    } catch (error) {
      logger.error('[Common] 房间加入失败:', error);
      // 如果是PeerConnection相关错误，等待一段时间后重试
      if (error instanceof Error && error.message.includes('connectionState')) {
        logger.debug('[Common] 检测到PeerConnection错误，等待2秒后重试...');
        await sleep(2000);
        await joinRoomWithTimeout();
      } else {
        throw error;
      }
    }
  };

  async function disPatchJoin(): Promise<boolean | undefined> {
    if (joining) {
      logger.info('[Common] 正在加入中，跳过重复请求');
      return false;
    }

    // 进入对话：先展示过渡遮罩，避免“准备连接”阶段的空白闪烁
    // 进入对话：不使用全局遮罩，改由 EntryStandby 内部 skeleton/状态提示承接，避免被遮罩的白屏

    const isSupported = await VERTC.isSupported();
    if (!isSupported) {
      Modal.error({
        title: '不支持 RTC',
        content: '您的浏览器可能不支持 RTC 功能，请尝试更换浏览器或升级浏览器后再重试。',
      });
      overlayManager.hide();
      return false;
    }

    setJoining(true);
    setConnectionState(ConnectionState.CONNECTING);

    // 初始化 sessionStorage 中的语音打断状态
    const initializeInterruptState = () => {
      const storedPref =
        typeof window !== 'undefined' ? sessionStorage.getItem('voiceInterruptAllowed') : null;
      if (storedPref === null) {
        // 如果 sessionStorage 中没有值，设置默认值
        sessionStorage.setItem('voiceInterruptAllowed', '0'); // 默认关闭
        logger.debug('[Common] 初始化语音打断状态为默认值: false');
      } else {
        logger.debug('[Common] 使用已存储的语音打断状态:', storedPref === '1');
      }
    };

    initializeInterruptState();

    try {
      // -------- Step 0: ensure we have static scene & rtc configs --------
      try {
        const stateNow = store.getState();
        if (Object.keys(stateNow.room.rtcConfigMap || {}).length === 0) {
          // 首次加载时，仅拉取静态场景配置与基础 RTC 映射，不在此处决定场景
          const { default: Apis } = await import('@/app/index');
          const {
            scenes,
          }: {
            scenes: {
              rtc: RTCConfig;
              scene: SceneConfig;
            }[];
          } = await Apis.Basic.getScenes();

          if (Array.isArray(scenes) && scenes.length) {
            dispatch(
              updateSceneConfig(
                scenes.reduce<Record<string, SceneConfig>>((prev, cur) => {
                  prev[cur.scene.id] = cur.scene;
                  return prev;
                }, {})
              )
            );
            // 只存储静态配置，不包含Token信息；不在此处写 basicInfo，由 Join 时机统一设置
            dispatch(
              updateRTCConfig(
                scenes.reduce<Record<string, RTCConfig>>((prev, cur) => {
                  prev[cur.scene.id] = cur.rtc;
                  return prev;
                }, {})
              )
            );
          }
        }
      } catch (e) {
        logger.warn('[Common] 拉取静态场景配置失败，可继续，但首次 join 可能报错', e);
      }

      // 在尝试获取 Token 之前，确保 scene 已写入（等待 URL Hook 生效）
      const waitForScene = (retryCount = 0): Promise<string | null> =>
        new Promise((resolve) => {
          const maxRetries = 10;
          const retryDelay = 100;
          const current = store.getState().room.scene;
          if (current) {
            resolve(current);
            return;
          }
          if (retryCount >= maxRetries) {
            resolve(null);
            return;
          }
          setTimeout(() => waitForScene(retryCount + 1).then(resolve), retryDelay);
        });

      const currentScene = await waitForScene();
      if (!currentScene) {
        Modal.error({
          title: '缺少场景',
          content: '未检测到场景，请通过 URL 或页面选择一个场景后再尝试连接。',
        });
        overlayManager.hide();
        setJoining(false);
        return false;
      }

      // 1) 只为当前场景获取Token；由 Join 路径统一更新 RtcClient.basicInfo

      // 每次 join 都强制拉新 token
      if (currentScene) {
        const tokenError = await getTokenWithRetry(
          async () => {
            const { default: Apis } = await import('@/app/index');
            const result = await Apis.Basic.getSceneToken({ scene: currentScene });
            if (result.rtc) {
              dispatch(
                updateRTCConfig({
                  ...store.getState().room.rtcConfigMap,
                  [currentScene]: result.rtc,
                })
              );
              // 确保 basicInfo 被初始化
              if (!RtcClient.basicInfo) {
                RtcClient.basicInfo = {} as any;
              }

              if (result.rtc.Token) {
                RtcClient.basicInfo.token = result.rtc.Token;
                RtcClient.basicInfo.room_id = result.rtc.RoomId;
                RtcClient.basicInfo.user_id = result.rtc.UserId;
                RtcClient.basicInfo.app_id = result.rtc.AppId;
                logger.debug('[Common] RtcClient.basicInfo 已完整设置 (敏感信息已隐藏)');
              }
            }
          },
          3,
          2000
        );
        if (tokenError !== true) {
          Modal.error({
            title: '连接失败',
            content: '网络异常，请刷新页面或联系客服。',
            okText: '刷新页面',
            onOk: () => {
              try {
                overlayManager.fadeInThen(() => window.location.reload(), '正在刷新…');
              } catch {
                window.location.reload();
              }
            },
          });
          setJoining(false);
          return false;
        }
      }

      // 检查 RTC 配置是否已设置
      const finalConfig = store.getState().room.rtcConfigMap[currentScene];
      if (!finalConfig?.AppId || !RtcClient.basicInfo?.token) {
        throw new Error('无法获取 RTC 配置信息');
      }

      // 确保 RtcClient.basicInfo 所有字段都被正确设置
      if (!RtcClient.basicInfo) {
        RtcClient.basicInfo = {} as any;
      }
      // 确保所有必要字段都存在
      if (!RtcClient.basicInfo.app_id) {
        RtcClient.basicInfo.app_id = finalConfig.AppId;
      }
      if (!RtcClient.basicInfo.room_id) {
        RtcClient.basicInfo.room_id = finalConfig.RoomId;
      }
      if (!RtcClient.basicInfo.user_id) {
        RtcClient.basicInfo.user_id = finalConfig.UserId;
      }
      if (!RtcClient.basicInfo.token) {
        RtcClient.basicInfo.token = finalConfig.Token;
      }

      logger.debug('[Common] 最终检查 RtcClient.basicInfo 完整性:', {
        hasAppId: !!RtcClient.basicInfo.app_id,
        hasRoomId: !!RtcClient.basicInfo.room_id,
        hasUserId: !!RtcClient.basicInfo.user_id,
        hasToken: !!RtcClient.basicInfo.token,
      });

      /** 1. Create RTC Engine with timeout */
      await safeEngineCreation();

      /** 2.1 Set events callbacks */
      RtcClient.addEventListeners(listeners);

      /** 2.2 RTC starting to join room with timeout */
      await safeRoomJoin();

      /** 3. Set users' devices info */
      const mediaDevices = await RtcClient.getDevices({
        audio: true,
        video: false,
      });

      dispatch(
        localJoinRoom({
          roomId: RtcClient.basicInfo.room_id,
          user: {
            username: RtcClient.basicInfo.user_id,
            userId: RtcClient.basicInfo.user_id,
            publishAudio: true, // 默认开启麦克风，与RTC的isAutoPublish一致
          },
        })
      );
      dispatch(
        updateSelectedDevice({
          selectedMicrophone: mediaDevices.audioInputs[0]?.deviceId,
        })
      );
      dispatch(updateMediaInputs(mediaDevices));

      // 启动音频采集，确保麦克风默认开启
      try {
        await RtcClient.startAudioCapture();
        logger.info('[Common] 音频采集已启动，麦克风默认开启');
      } catch (error) {
        logger.warn('[Warn] 启动音频采集失败:', error);
      }

      handleAIGCModeStart();
      setConnectionState(ConnectionState.CONNECTED);

      // 连接完成后淡出遮罩
      overlayManager.hide();
    } catch (error) {
      if (
        typeof error === 'object' &&
        error &&
        'message' in error &&
        (error as any).message === '无法获取 RTC 配置信息'
      ) {
        overlayManager.hide();
        setJoining(false);
        return false;
      }
      logger.error('[Common] RTC 连接失败:', error);
      setConnectionState(ConnectionState.ERROR);
      try {
        if (RtcClient.engine) {
          await RtcClient.leaveRoom?.();
        }
      } catch (cleanupError) {
        logger.warn('[Common] 清理RTC资源失败:', cleanupError);
      }
      // 出错时先淡出遮罩，以便用户看到错误弹窗
      try {
        overlayManager.hide();
      } catch {}
      Modal.error({
        title: 'RTC 连接失败',
        content: `连接失败: ${(error as any).message}`,
      });
    } finally {
      setJoining(false);
    }
  }

  // 设置 ref 引用
  disPatchJoinRef.current = disPatchJoin;

  return [joining, disPatchJoin];
};

export const useLeave = () => {
  const dispatch = useDispatch();
  const { id } = useScene();

  return async function () {
    try {
      // 数据备份在 ToolBar 组件中处理
      // const botName = useScene().botName;
      // const feedbackToken = RtcClient.basicInfo?.token;
      // const feedbackRoomId = RtcClient.basicInfo?.room_id;
      // const msgHistory = room.msgHistory;
      // const sceneConfig = room.sceneConfigMap[room.scene];

      try {
        // 检查引擎状态
        if (RtcClient.engine && typeof RtcClient.engine === 'object' && RtcClient.engine !== null) {
          await Promise.all([
            RtcClient.stopAudioCapture?.(),
            RtcClient.stopScreenCapture?.(),
            RtcClient.stopVideoCapture?.(),
          ]);
        } else {
          logger.warn('[Common] 引擎未就绪，跳过停止采集操作');
        }
      } catch (e) {
        logger.warn('[Common] RTC stop capture error', e);
      }
      try {
        // 确保有有效的场景ID和RoomId
        if (id && (RtcClient.basicInfo?.room_id || RtcClient.lastRoomId)) {
          await RtcClient.stopAgent?.(id);
        } else {
          logger.warn('[Common] 跳过stopAgent，缺少必要参数');
        }
      } catch (e) {
        logger.warn('[Common] RTC stop agent error', e);
      }
      try {
        // 检查引擎状态后再调用leaveRoom
        if (RtcClient.engine && typeof RtcClient.engine === 'object' && RtcClient.engine !== null) {
          await RtcClient.leaveRoom?.();
        } else {
          logger.warn('[Common] 引擎未就绪，跳过leaveRoom操作');
        }
      } catch (e) {
        logger.warn('[Common] RTC leave room error', e);
      }

      // 反馈上传逻辑已移至其他组件
      // 新策略：每次退出都刷新页面，数据存储到 localStorage，在 Entry 页面统一上传
      /*
      setTimeout(() => {
        try {
          if (msgHistory.length > 0 && feedbackToken && feedbackRoomId) {
            const feedbackData = buildFeedbackData(msgHistory, botName, {
              name: sceneConfig?.name || '',
              roomId: feedbackRoomId,
              taskId: room.scene || '',
            });
            sendConversationFeedback(feedbackData, feedbackToken).catch((e) => {
              logger.warn('[Common] 反馈上传失败', e);
            });
          }
        } catch (e) {
          logger.warn('[Common] 反馈上传失败', e);
        }
      }, 0);
      */

      // 3. 清理 Redux 状态
      dispatch(resetRoomState());
      return true;
    } catch (e) {
      logger.warn('[Common] leaveRoom unexpected error', e);
      return false;
    }
  };
};
